import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Church, HeartHandshake, Palette, Store, ArrowRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Reveal } from "@/components/Reveal";
import { Eyebrow } from "@/components/form";
import { usePageMeta } from "@/hooks/usePageMeta";
import { api, fileUrl } from "@/lib/api";

const HERO_IMG = "/hero-stained-glass.png";

const PILLARS = [
  { icon: Church, title: "Preserving Places of Worship", text: "Acquiring closing church properties and exploring opportunities for continued Kingdom use.", img: "https://images.unsplash.com/photo-1624573830079-7fd9ce354be5?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: HeartHandshake, title: "Strengthening Ministries", text: "Supporting Christian ministries as they grow and reach their communities.", img: "https://images.unsplash.com/photo-1628717341663-0007b0ee2597?crop=entropy&cs=srgb&fm=jpg&q=85&w=800" },
  { icon: Palette, title: "Championing Christian Artists", text: "Helping artists gain visibility, build connections, and find support.", img: "/artists-worship.jpg" },
  { icon: Store, title: "Connecting Kingdom Businesses", text: "Helping believers discover and support Christ-centered businesses.", img: "/business-paintking.png", href: "/directory" },
];

export default function Home() {
  usePageMeta("Strengthening the Body. Advancing the Kingdom.",
    "Lord Almighty supports churches, ministries, artists, and Christ-centered businesses so faith can flourish and more lives can be reached with the love of Jesus.");
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/businesses", { params: { featured: true } })
      .then((r) => setFeatured(r.data.businesses.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden bg-navy text-ivory">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Stained glass church windows" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy/85 via-navy/45 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
        </div>

        {/* playful floating glows */}
        <motion.div className="pointer-events-none absolute top-16 right-[12%] h-40 w-40 rounded-full bg-gold/30 blur-3xl"
          animate={{ scale: [1, 1.2, 1], opacity: [0.4, 0.7, 0.4] }} transition={{ duration: 5, repeat: Infinity }} />

        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-36">
          <motion.div
            initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
            className="max-w-3xl"
          >
            <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-semibold leading-[1.05] tracking-tight drop-shadow-[0_10px_30px_rgba(10,20,40,0.6)]">
              Strengthening the Body.<br />
              <span className="text-gold">Advancing the Kingdom.</span>
            </h1>
            <p className="mt-7 text-base sm:text-lg text-ivory/90 leading-relaxed max-w-2xl drop-shadow-[0_4px_16px_rgba(10,20,40,0.55)]">
              Supporting churches, ministries, artists, and Christ-centered businesses so faith can
              flourish and more lives can be reached with the love of Jesus.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Button asChild className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-8 py-6 text-base shadow-2xl transition-transform hover:scale-105">
                <Link to="/mission" data-testid="hero-explore-btn">Explore Our Mission <ArrowRight className="ml-2 h-4 w-4" /></Link>
              </Button>
              <Button asChild variant="outline"
                className="rounded-full border-gold text-ivory bg-navy/30 backdrop-blur-sm hover:bg-gold/20 px-8 py-6 text-base transition-transform hover:scale-105">
                <Link to="/connect" data-testid="hero-partner-btn">Partner With Us</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PILLARS */}
      <section className="relative mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        {/* floating mascot — hovers up & to the right, no background, out of layout flow */}
        <motion.img
          src="/jesus-dove.png" alt="A friendly claymation figure of Jesus holding a dove"
          className="pointer-events-none hidden lg:block absolute right-0 xl:right-4 -top-6 w-44 xl:w-56 object-contain drop-shadow-[0_22px_44px_rgba(10,20,40,0.28)] z-10"
          animate={{ y: [0, -12, 0], rotate: [-1.5, 1.5, -1.5] }}
          transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        />
        <Reveal className="max-w-2xl">
          <Eyebrow>What we do</Eyebrow>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl font-semibold text-navy tracking-tight">
            Many ways we serve the body of Christ
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            We believe churches, ministries, artists, and businesses can work together so faith
            flourishes and communities are strengthened.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {PILLARS.map((p, i) => {
            const tones = [
              { ring: "border-t-emerald", chip: "bg-emerald/15 text-emerald" },
              { ring: "border-t-gold", chip: "bg-gold/20 text-gold-dark" },
              { ring: "border-t-navy-700", chip: "bg-navy/10 text-navy-700" },
              { ring: "border-t-ruby", chip: "bg-ruby/10 text-ruby" },
            ][i % 4];
            const CardTag = p.href ? Link : "div";
            const cardProps = p.href
              ? { to: p.href, "data-testid": "pillar-directory-link" }
              : {};
            return (
              <Reveal key={p.title} delay={i * 0.08}>
                <CardTag {...cardProps} className={`group h-full flex flex-col rounded-2xl bg-white border border-border border-t-4 ${tones.ring} overflow-hidden shadow-[0_8px_32px_rgba(27,42,71,0.06)] transition-all duration-300 hover:-translate-y-2 hover:shadow-[0_20px_50px_rgba(27,42,71,0.14)] ${p.href ? "cursor-pointer" : ""}`}>
                  <div className="relative h-40 overflow-hidden">
                    <img src={p.img} alt={p.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/25 to-transparent" />
                  </div>
                  <div className="relative flex flex-1 flex-col p-7 pt-0">
                    <span className={`-mt-8 inline-flex h-16 w-16 items-center justify-center rounded-2xl ${tones.chip} ring-4 ring-white shadow-md transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-6`}>
                      <p.icon className="h-7 w-7" />
                    </span>
                    <h3 className="mt-4 font-display text-2xl font-semibold text-navy">{p.title}</h3>
                    <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{p.text}</p>
                    {p.href && (
                      <span className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-emerald group-hover:gap-3 transition-all">
                        Browse the Directory <ArrowRight className="h-4 w-4" />
                      </span>
                    )}
                  </div>
                </CardTag>
              </Reveal>
            );
          })}
        </div>
      </section>

      {/* MISSION STORY / THE CHOSEN */}
      <section className="bg-ivory-alt">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-24 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <Eyebrow>Our story</Eyebrow>
            <h2 className="mt-3 font-display text-4xl font-semibold text-navy tracking-tight">
              A mission rooted in stewardship
            </h2>
            <p className="mt-5 text-muted-foreground leading-relaxed">
              Lord Almighty exists to strengthen the body of Christ and advance God's Kingdom. When a
              congregation faces closure, a building need not fall silent — it can continue to be a
              place where the Gospel is proclaimed. Where ministries and artists are growing, they
              can find encouragement and connection. And where believers do business, faith can shape
              how they serve.
            </p>
            <p className="mt-4 text-muted-foreground leading-relaxed">
              We are also the founders of Jesus United, and we have been glad to support The Chosen
              series — a modest part of our story, not an official partnership or endorsement.
            </p>
            <Button asChild variant="outline" className="mt-8 rounded-full border-gold text-navy hover:bg-gold/10 px-7">
              <Link to="/mission">Read our mission</Link>
            </Button>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative rounded-2xl overflow-hidden shadow-[0_20px_60px_rgba(27,42,71,0.15)]">
              <img
                src="https://images.unsplash.com/photo-1561114892-c6e759d7cf2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwxfHxjaHJpc3RpYW4lMjBtaW5pc3RyeSUyMHdvcmt8ZW58MHx8fHwxNzkwNjA2MzcyfDA&ixlib=rb-4.1.0&q=85"
                alt="A person in prayer" className="w-full h-[420px] object-cover" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* DIRECTORY FEATURE */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 lg:py-32">
        <Reveal className="text-center max-w-2xl mx-auto">
          <Eyebrow>Kingdom Business Directory</Eyebrow>
          <h2 className="mt-3 font-display text-4xl lg:text-5xl font-semibold text-navy tracking-tight">
            Find Businesses That Share Your Faith.
          </h2>
          <p className="mt-4 text-muted-foreground leading-relaxed">
            Supporting Christ-centered businesses helps strengthen our communities and equips owners
            to serve, give, and contribute to the advancement of God's Kingdom.
          </p>
        </Reveal>

        {featured.length > 0 ? (
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {featured.map((b, i) => (
              <Reveal key={b.id} delay={i * 0.08}>
                <Link to={`/directory/${b.id}`} className="block h-full rounded-xl bg-white border border-border p-6 shadow-sm hover:shadow-md hover:-translate-y-1 transition-transform">
                  {b.is_demo && <Badge className="mb-3 bg-gold/15 text-gold-dark border-none">Demo</Badge>}
                  <h3 className="font-display text-2xl text-navy font-semibold">{b.name}</h3>
                  <Badge variant="outline" className="mt-2 border-emerald/30 text-emerald">{b.category}</Badge>
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-3">{b.description}</p>
                  <p className="mt-4 flex items-center gap-1.5 text-xs text-navy/60"><MapPin className="h-3.5 w-3.5" /> {b.city}, {b.state}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        ) : (
          <Reveal className="mt-12">
            <div className="rounded-2xl border border-dashed border-gold/40 bg-white/60 p-12 text-center">
              <p className="font-display text-2xl text-navy">The directory is being prepared</p>
              <p className="mt-2 text-muted-foreground max-w-lg mx-auto">
                As Christ-centered businesses are submitted and reviewed, they will appear here. Own a
                business? We'd love to hear from you.
              </p>
            </div>
          </Reveal>
        )}

        <div className="mt-12 flex flex-col sm:flex-row justify-center gap-4">
          <Button asChild className="rounded-full bg-emerald hover:bg-emerald-dark text-white px-8">
            <Link to="/directory" data-testid="home-browse-directory">Browse the Directory</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full border-gold text-navy hover:bg-gold/10 px-8">
            <Link to="/directory?submit=1">Submit Your Business</Link>
          </Button>
        </div>
      </section>

      {/* JESUS UNITED */}
      <section className="relative overflow-hidden text-ivory">
        <div className="absolute inset-0">
          <img src="/ju-jerusalem.webp" alt="Jesus United — the JUniverse Jerusalem scene" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/30" />
          <div className="absolute inset-0 bg-gradient-to-t from-navy/60 via-transparent to-navy/20" />
        </div>
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 py-28 lg:py-36">
          <Reveal className="max-w-xl">
            <Eyebrow>Evangelizing, making disciples, learning and uniting the body of Christ</Eyebrow>
            <h2 className="mt-3 font-display text-4xl lg:text-5xl font-semibold tracking-tight">Jesus United</h2>
            <p className="mt-5 text-ivory/85 leading-relaxed text-base sm:text-lg">
              Founded by the founders of Lord Almighty, Jesus United is a ministry focused on
              children's ministry, adult Bible discipleship, spiritual growth, and evangelism —
              home to the JUniverse, where every life has a story.
            </p>
            <Button asChild className="mt-8 rounded-full bg-gold hover:bg-gold-dark text-navy px-8 py-6 text-base shadow-2xl">
              <Link to="/jesus-united" data-testid="home-jesusunited-link">
                Learn More <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </Reveal>
        </div>
      </section>

      {/* CLOSING INVITATION */}
      <section className="relative overflow-hidden bg-ivory-alt">
        <div className="pointer-events-none absolute -top-20 left-1/2 -translate-x-1/2 h-72 w-[36rem] rounded-full bg-gold/20 blur-3xl" />
        <div className="relative mx-auto max-w-4xl px-5 lg:px-8 py-24 lg:py-32 text-center">
          <Reveal>
            <motion.img src="/jesus-dove.png" alt="A friendly claymation figure of Jesus holding a dove"
              className="mx-auto h-40 lg:h-52 object-contain drop-shadow-[0_20px_40px_rgba(197,160,89,0.35)]"
              animate={{ y: [0, -12, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} />
            <h2 className="mt-6 font-display text-4xl lg:text-5xl font-semibold text-navy tracking-tight">
              Let's build something that lasts
            </h2>
            <p className="mt-4 text-muted-foreground leading-relaxed max-w-xl mx-auto">
              Whether you lead a church, serve in ministry, create as an artist, or run a business —
              there is a place for you in this work.
            </p>
            <Button asChild className="mt-9 rounded-full bg-emerald hover:bg-emerald-dark text-white px-10 py-6 text-base shadow-2xl transition-transform hover:scale-105">
              <Link to="/connect" data-testid="home-closing-connect">Start a Conversation</Link>
            </Button>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
